import {
  TableColumnType as AntdTableColumnType,
  TableProps as AntdTableProps,
} from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import { useColumnsSelect } from './ColumnsControl/useColumnsSelect';
import { useLocalColumnsSelect } from './ColumnsControl/useLocalColumnsSelect';
import { TableContent } from './TableContent';

export type TableColumnType<T> = Omit<AntdTableColumnType<T>, 'key'> & {
  key: string;
  children?: ColumnsType<T>;
  exportValue?: (item: T) => React.ReactNode;
};

export interface TableProps<T>
  extends Omit<AntdTableProps<T>, 'columns' | 'title'> {
  name: string;
  actions?: React.ReactElement;
  rightActions?: React.ReactElement;
  columns: TableColumnType<T>[];
  hideDefaultActions?: boolean;
  useVirtualList?: boolean;
  onDataSourceChange?: () => void;
  onFiltersReset?: () => void;
  defaultHiddenColumnKeys?: string[];
  hideResetFiltersAction?: boolean;
  responsiveCardView?: boolean;
  columnSettings?: 'server' | 'local';
  hideExportAction?: boolean;
}

export function Table<T extends object>(props: TableProps<T>) {
  const columnSettings = props.columnSettings ?? 'server';

  if (columnSettings === 'local') {
    return <LocalSettingsTable {...props} />;
  }

  return <ServerSettingsTable {...props} />;
}

function ServerSettingsTable<T extends object>(props: TableProps<T>) {
  const { visibleColumns, columnsSelect, isLoading } = useColumnsSelect(
    props.name,
    props.columns,
    props.defaultHiddenColumnKeys,
  );

  return (
    <TableContent
      {...props}
      visibleColumns={visibleColumns as TableColumnType<T>[]}
      columnsSelect={columnsSelect}
      isColumnsLoading={isLoading}
    />
  );
}

function LocalSettingsTable<T extends object>(props: TableProps<T>) {
  const { visibleColumns, columnsSelect, isLoading } = useLocalColumnsSelect(
    props.name,
    props.columns,
    props.defaultHiddenColumnKeys,
  );

  return (
    <TableContent
      {...props}
      visibleColumns={visibleColumns as TableColumnType<T>[]}
      columnsSelect={columnsSelect}
      isColumnsLoading={isLoading}
    />
  );
}
